import type { GameCatalogEntry, SessionSummary } from '@perplexicon/api-client';

import {
  assetUrl,
  resolveCatalogBanner,
  resolveGameProfileArt,
  type AssetName,
} from '../../../assets/asset-manifest.js';
import {
  GameActionLink,
  GameButton,
  GameCardFrame,
  OrnamentalRule,
  StatusChip,
  deriveGameCardState,
} from '../../../components/index.js';
import {
  GAME_CATALOG_ART,
  GAME_CATALOG_SUMMARY,
  GAME_PROFILE_ART,
} from '../../../games/registry.js';
import { gameMetaEntries, type GameMetaKind } from '../../../lib/game-meta.js';
import { PLATE_GAME_NAME } from '../../../styles/plate-type.js';
import { GameModeAction } from './GameModeAction.js';

export interface GameCatalogCardProps {
  game: GameCatalogEntry;
  onPlaySolo: () => void;
  isStarting: boolean;
  /** Opens the create-multiplayer-game flow (APP_SPEC §19-§20). Omitted where the caller doesn't offer it, or the game supports no invitable two-player mode. */
  onPlayVersus?: () => void;
  /** Opens the create-lobby flow (docs/games/word-rush/GAME_PLAN.md §11, §13.9, W1). Omitted where the caller doesn't offer it, or the game does not qualify per `canHostLobby`. */
  onHostLobby?: () => void;
  /** The viewer's own in-progress session for this game (`activeSessionsByGame`), when they have one — never a hardcoded flag (APP_SPEC §18). */
  activeSession?: SessionSummary | null;
  headingLevel?: 2 | 3;
  /**
   * Horizontal composition for the final card when the visible game count is
   * odd (approved Games-page mockup "WIDE FINAL CARD DESIGN"). The caller
   * decides this from the actual rendered game array — never hardcoded to a
   * specific game — and it only changes layout at `tablet`+, where the grid
   * is actually two columns; below that this renders identically to
   * `'normal'`.
   */
  layout?: 'normal' | 'wide';
}

interface ModeEntry {
  key: keyof typeof MODE_ICON_CLASS;
  icon: string;
  buttonLabel: string;
  onClick: () => void;
}

/**
 * The rendered box for each mode glyph.
 *
 * The CSS dimension is never the size the player actually sees: each master
 * carries its own transparent margin, so the drawn symbol is some fraction of
 * the box. What matters is that the *drawn* glyph lands at ~71px on desktop for
 * every mode, which is what makes the three read as one set.
 *
 * The current masters are drawn to a consistent scale — each fills 83-85% of
 * its 1254² canvas in both axes — so one shared box satisfies that for all
 * three. This stays a per-mode map rather than a single constant because that
 * consistency is a property of the artwork, not a guarantee: the previous set
 * ranged from 71% to 92%, and needed three different boxes to look like three
 * of the same thing. If a replacement master is drawn to a different scale,
 * correct it here rather than accepting a glyph that reads larger or smaller
 * than its neighbours.
 *
 * Keyed by launch mode, which is a platform concept (`supportedModes`), so
 * nothing here knows which game it is (CLAUDE.md §2).
 */
const MODE_ICON_BOX =
  'h-[70px] w-[70px] tablet:h-[80px] tablet:w-[80px] desktop:h-[90px] desktop:w-[90px]';

/**
 * How far each glyph is pulled down into its button.
 *
 * This is the one number that genuinely cannot be shared, because it is
 * measured against something the box size does not describe: the transparent
 * canvas each master carries *below* its drawn circle. As a fraction of canvas
 * height that padding is 7.97% (solo), 7.81% (versus) and 9.09% (multiplayer),
 * so at a 90px box the circles sit 7.2px, 7.0px and 8.2px above the bottom of
 * their own boxes. A single shared offset therefore seats three circles at
 * three different depths — which is exactly what it did: a uniform -12px box
 * offset produced only 3.8-5.0px of *visible* overlap, and left the
 * multiplayer circle sitting a pixel higher than the other two.
 *
 * Each value below is the padding plus the ~10px of real overlap wanted, and
 * every one is a whole pixel. The result is 9.8-10.0px of visible overlap on
 * all three and a shared apparent baseline. Judge any change to these against
 * the rendered circle, never the PNG box.
 */
const MODE_ICON_CLASS = {
  solo: `${MODE_ICON_BOX} -mb-[16px] tablet:-mb-[16px] desktop:-mb-[17px]`,
  versus: `${MODE_ICON_BOX} -mb-[16px] tablet:-mb-[16px] desktop:-mb-[17px]`,
  host: `${MODE_ICON_BOX} -mb-[16px] tablet:-mb-[17px] desktop:-mb-[18px]`,
} as const;

/** `GameMetaKind -> its glyph`, so the card never matches on the rendered chip text. */
const META_ICON: Record<GameMetaKind, AssetName> = {
  solo: 'gameMetaSolo',
  players: 'gameMetaPlayers',
  realtime: 'gameMetaRealtime',
};

/**
 * The metadata glyphs, which fill only ~39-45% of their own canvas and so need
 * a box roughly twice the size they appear. The negative block margin cancels
 * that surplus: without it a 44px box would set a 44px metadata line to carry a
 * ~19px mark, and the card would pay 20px of height for nothing.
 */
const META_ICON_CLASS = 'h-11 w-11 -my-2.5 shrink-0 object-contain';

/**
 * How many columns the mode groups are laid out in. A *grid* rather than a
 * wrapping flex row, because the groups then share the available width and
 * shrink together: two controls side by side inside one card used to overflow
 * the content box at tablet widths and wrap onto two rows, which doubled the
 * card's height at exactly one breakpoint. Columns are keyed off the length of
 * the rendered mode list, so nothing here knows which game it is (CLAUDE.md
 * §2). Three modes fall back to 2×2 until there is room for a true
 * three-across row.
 */
const MODE_COLUMNS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-2 desktop:grid-cols-3',
};

/**
 * The dominant "Start a Game" card (approved Games-page mockup) — an
 * illustrated banner with the game's emblem and name laid over it, a parchment
 * body with its summary, metadata, and one launch control per playable mode,
 * all inside the shared brass `GameCardFrame`.
 *
 * Every region of the card is deliberately *bounded*, because a row of these
 * has to end at one Y coordinate without any card being padded out with dead
 * parchment to get there (approved mockup "ROW HEIGHT CONSISTENCY"): the banner
 * is a fixed height, the summary is a fixed two-line region, the metadata is
 * one line, and the mode block is the same shape for one mode or three. The
 * card then stretches to its row and the mode block sits on the bottom edge
 * (`mt-auto`), so what little slack a row ever has collects in one place rather
 * than opening a gap in the middle of every card.
 *
 * Its materials are the profile's game cards': the same frame variant, the same
 * Alegreya at the same sizes for the game's name, and the same gold ornamental
 * rule the plate headings use to divide a region. Replaces the flatter
 * `GameCard` for this page only; `GameCard` still renders on the dashboard,
 * which wants a smaller, denser card.
 *
 * Every control keeps the exact accessible names and `data-testid` the
 * platform's own catalog tests assert on ("Play Solo" -> "Solo" and
 * "Host Game" -> "Host" once a session is active, "Continue Game" to resume)
 * — only the visual treatment changes here, never the contract.
 */
export function GameCatalogCard({
  game,
  onPlaySolo,
  isStarting,
  onPlayVersus,
  onHostLobby,
  activeSession,
  headingLevel = 2,
  layout = 'normal',
}: GameCatalogCardProps) {
  const { canStartSolo, showPlayVersus, showHostLobby, hasLaunchAction, isVisible, statusChip } =
    deriveGameCardState(game, !!onPlayVersus, !!onHostLobby);
  const Heading = headingLevel === 3 ? 'h3' : 'h2';
  const meta = gameMetaEntries(game);
  // The banner crop is the game's own (`GAME_CATALOG_ART`), declared beside its
  // artwork; the emblem is the game's profile emblem reused, so a game needs no
  // catalog-only emblem slot. Neither is branched on here (CLAUDE.md §2).
  const banner = resolveCatalogBanner(GAME_CATALOG_ART[game.gameId]);
  const emblem = resolveGameProfileArt(undefined, GAME_PROFILE_ART[game.gameId]).icon;
  // Presentation copy where the game supplies it, its real description
  // otherwise — see `GAME_CATALOG_SUMMARY`.
  const summary = GAME_CATALOG_SUMMARY[game.gameId] ?? game.description;
  const isWide = layout === 'wide';

  // The banner is a background layer rather than an `<img>`: the crop needs a
  // real zoom (`background-size`) to bring an off-centre subject up in a band
  // this shallow, and `object-fit: cover` offers no way to express that. It
  // also sidesteps the flex stretch trap an `<img>` brings — a percentage-
  // height image inside an auto-height flex item can't resolve that percentage
  // and falls back to its own aspect ratio, forcing the whole row taller.
  const bannerClasses = isWide
    ? 'h-[150px] tablet:h-auto tablet:w-[38%]'
    : 'h-[132px] tablet:h-[138px] desktop:h-[142px]';

  // One list rather than three near-duplicate JSX blocks: the glyph represents
  // the mode, the button label the action, and — once a session is active —
  // "Play Solo" and "Host Game" shorten to "Solo"/"Host" the same way they
  // always have (CLAUDE.md §2: still derived from platform metadata, no gameId
  // branch).
  const modes: ModeEntry[] = [];
  if (canStartSolo) {
    modes.push({
      key: 'solo',
      icon: assetUrl('gameModeSolo'),
      buttonLabel: activeSession ? 'Solo' : 'Play Solo',
      onClick: onPlaySolo,
    });
  }
  if (showPlayVersus) {
    modes.push({
      key: 'versus',
      icon: assetUrl('gameModeVersus'),
      // "Challenge", not "Play vs Friend" (approved Games-page mockup copy).
      buttonLabel: 'Challenge',
      // deriveGameCardState only sets showPlayVersus when onPlayVersus was
      // itself truthy (its `hasPlayVersus` argument is `!!onPlayVersus`),
      // but that guarantee lives behind a function call now, not a literal
      // `!!onPlayVersus` in this scope, so TS can no longer narrow it here.
      onClick: onPlayVersus as () => void,
    });
  }
  if (showHostLobby) {
    modes.push({
      key: 'host',
      icon: assetUrl('gameModeMultiplayer'),
      buttonLabel: activeSession ? 'Host' : 'Host Game',
      // Same guarantee as onPlayVersus above, via hasHostLobby.
      onClick: onHostLobby as () => void,
    });
  }

  const modeColumns = MODE_COLUMNS[Math.min(modes.length, 3)] ?? 'grid-cols-2';
  const modeGridClass = `grid w-full justify-items-center gap-x-4 gap-y-3 ${modeColumns}`;
  // A lone mode gets the full-width control the mockup shows; two share the
  // row, so each is capped short of half the card to keep a real gutter
  // between them.
  const modeWidthClass = modes.length === 1 ? 'max-w-[15rem]' : 'max-w-[12.5rem]';

  const metaList = meta.length > 0 && (
    <ul
      aria-label={`${game.name} details`}
      className={`flex flex-wrap items-center gap-x-4 gap-y-1 font-profile text-[1rem] text-textPrimary/75 ${
        isWide ? 'justify-center tablet:justify-start' : 'justify-center'
      }`}
    >
      {meta.map((entry) => (
        <li key={entry.label} className="flex items-center gap-1.5">
          <img
            src={assetUrl(META_ICON[entry.kind])}
            alt=""
            aria-hidden="true"
            width={128}
            height={128}
            className={META_ICON_CLASS}
            draggable={false}
          />
          <span>{entry.label}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <GameCardFrame
      as="article"
      data-testid={`game-card-${game.gameId}`}
      className={`h-full min-w-0 ${isWide ? 'tablet:col-span-2' : ''}`}
    >
      <div
        // The wide card states its own height floor rather than inheriting the
        // normal card's: laid out horizontally its content is short, so without
        // this it collapses to a thin ~150px strip instead of reading as the
        // deliberate landscape panel that closes an odd-numbered grid.
        className={`flex h-full flex-col overflow-hidden rounded-md bg-parchment shadow-3 ring-1 ring-inset ring-borderStrong/30 ${isWide ? 'tablet:min-h-[204px] tablet:flex-row' : ''}`}
      >
        {/* Illustrated banner: the game's own catalog art, cropped to the
            zoom and anchor the game declares, with its emblem and name laid
            over it. */}
        <div
          className={`relative shrink-0 overflow-hidden bg-frame bg-no-repeat ${bannerClasses}`}
          style={{
            backgroundImage: `url("${banner.image}")`,
            backgroundSize: banner.size,
            backgroundPosition: banner.position,
          }}
        >
          <div className="absolute inset-0 bg-[linear-gradient(to_top,rgb(2_16_12/0.9)_0%,rgb(2_16_12/0.38)_46%,transparent_78%)]" />
          {/* Vignette, as on the profile's game cards: melts the banner's
              edges into the brass trim instead of ending on a hard rectangle. */}
          <div
            aria-hidden="true"
            className="absolute inset-0 shadow-[inset_0_0_26px_10px_rgb(3_20_15/0.5)]"
          />
          <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 p-3.5 tablet:gap-3.5 tablet:p-5">
            {/* The emblem keeps a square frame of its own whatever it holds.
                Every game's emblem is a full-bleed square tile with no alpha
                margin, so the box *is* the glyph — and a game still shipping a
                placeholder (a square crop of its own artwork rather than a
                designed mark) is contained by the same frame instead of
                reading as a stray photograph laid on the banner.
                `object-contain`, matching the profile card's header and
                `ResumeGameCard`: this was `object-cover`, which renders
                identically for the asset pipeline's square-in-square case
                but would silently crop rather than letterbox the day that
                stops being true, unlike every other emblem in the app. */}
            <span className="relative shrink-0 overflow-hidden rounded-md border border-borderStrong/50 shadow-[0_3px_10px_rgb(0_0_0/0.55)]">
              <img
                src={emblem}
                alt=""
                aria-hidden="true"
                width={216}
                height={216}
                className="block h-[72px] w-[72px] object-contain tablet:h-[84px] tablet:w-[84px] desktop:h-[92px] desktop:w-[92px]"
                draggable={false}
              />
            </span>
            {/* The same scale the profile's game card uses — this file's own
                doc claims "the same Alegreya at the same sizes for the game's
                name", which was not true: the two ran 0.8-2.4px apart. */}
            <Heading
              className={`min-w-0 truncate ${PLATE_GAME_NAME} text-textOnDark [text-shadow:0_2px_8px_rgb(0_0_0/0.8)]`}
            >
              {game.name}
            </Heading>
          </div>
        </div>

        {/* Parchment body. Every region is bounded, and the mode block is
            pushed to the bottom edge, so cards in one row finish level without
            any of them carrying a field of empty parchment. */}
        <div
          className={`flex flex-1 flex-col px-4 pb-3 pt-2.5 tablet:px-5 ${isWide ? 'tablet:flex-row tablet:items-center tablet:gap-7' : ''}`}
        >
          <div className={`min-w-0 ${isWide ? 'tablet:flex-1 tablet:text-left' : 'text-center'}`}>
            {(activeSession ?? statusChip) && (
              <div
                className={`mb-2 flex flex-wrap gap-1.5 ${isWide ? 'justify-center tablet:justify-start' : 'justify-center'}`}
              >
                {activeSession && <StatusChip tone="active">Active</StatusChip>}
                {statusChip && <StatusChip tone={statusChip.tone}>{statusChip.label}</StatusChip>}
              </div>
            )}

            {/* A fixed two-line region from `tablet` up: clamped so a long
                description can never set the height of its row, and floored so
                a short one can never let its card ride up above its neighbour.
                Neither applies on mobile, where the grid is a single column and
                so has no row to equalise — holding the desktop budget there
                only truncates a sentence mid-word for no benefit. */}
            <p
              className={`line-clamp-3 font-profile text-[1.05rem] leading-snug text-textPrimary/90 tablet:line-clamp-2 ${isWide ? '' : 'tablet:min-h-[46px]'}`}
            >
              {summary}
            </p>

            {metaList && (
              <>
                {/* The page's own gold rule, not a plain hairline — the same
                    ornament the plate headings divide with. */}
                <OrnamentalRule className="my-1.5 w-full" />
                {metaList}
              </>
            )}
          </div>

          {activeSession ? (
            <div
              className={`flex flex-col gap-2.5 ${isWide ? 'tablet:mt-0 tablet:w-auto tablet:shrink-0' : 'mt-auto pt-1'}`}
            >
              <GameActionLink
                to={`/play/${activeSession.id}`}
                className={isWide ? 'tablet:self-center' : 'self-center'}
              >
                Continue Game
                <span className="sr-only"> — {game.name}</span>
              </GameActionLink>
              {hasLaunchAction && (
                /* The secondary block on a card whose game is already in
                   progress. Deliberately the compact treatment — small glyphs,
                   `sm` controls, one caption — because it is an extra offer
                   below the real action, and because every pixel it adds is a
                   pixel of empty parchment its row-neighbour has to grow to
                   match. */
                <div className="flex flex-col items-center gap-1.5 border-t border-borderStrong/20 pt-2.5">
                  <p className="font-profile text-[0.8rem] font-semibold uppercase tracking-[0.055em] text-textMuted">
                    Play another
                  </p>
                  <div className={modeGridClass}>
                    {modes.map((mode) => (
                      <GameModeAction
                        key={mode.key}
                        icon={mode.icon}
                        iconClassName="h-9 w-9 -mb-[7px] shrink-0 object-contain"
                        className="max-w-[10rem]"
                      >
                        <GameButton
                          size="sm"
                          onClick={mode.onClick}
                          isLoading={mode.key === 'solo' && isStarting}
                        >
                          {mode.buttonLabel}
                        </GameButton>
                      </GameModeAction>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : hasLaunchAction ? (
            <div
              className={`${modeGridClass} ${isWide ? 'tablet:mt-0 tablet:w-auto tablet:shrink-0' : 'mt-auto pt-1'}`}
            >
              {modes.map((mode) => (
                <GameModeAction
                  key={mode.key}
                  icon={mode.icon}
                  iconClassName={MODE_ICON_CLASS[mode.key]}
                  className={modeWidthClass}
                >
                  <GameButton onClick={mode.onClick} isLoading={mode.key === 'solo' && isStarting}>
                    {mode.buttonLabel}
                  </GameButton>
                </GameModeAction>
              ))}
            </div>
          ) : (
            <p
              className={`font-profile text-[0.95rem] text-textMuted ${isWide ? 'tablet:mt-0' : 'mt-auto pt-1 text-center'}`}
            >
              {isVisible ? 'No playable mode available right now.' : 'Currently unavailable.'}
            </p>
          )}
        </div>
      </div>
    </GameCardFrame>
  );
}
